import React, { useEffect, useState } from "react";
import BillingPrintDocument from "../../components/BillingPrintDocument";
import { BillingVisit } from "./billingTypes";
import { BACKEND_URL } from "../../config";

interface BillItem {
  name: string;
  quantity: number;
  price: number;
  totalAmount: number;
  medicine: any;
  item_type?: string;
  description?: string;
}

interface Bill {
  id: number;
  patient_id: number;
  patientName: string;
  date: string;
  items: BillItem[];
  totalAmount: number;
}

interface Patient {
  id: number;
  contact: any;
  Contact?: any;
}

interface Biller {
  id: number;
  name: string;
  displayName?: string;
  email?: string;
}

interface PrintBillWrapperProps {
  billId: number;
  autoPrint?: boolean;
}

const PrintBill: React.FC<PrintBillWrapperProps> = ({ billId, autoPrint = false }) => {
  const [visit, setVisit] = useState<BillingVisit | null>(null);
  const [error, setError] = useState("");
  const [bill, setBill] = useState<Bill | null>(null);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [biller, setBiller] = useState<Biller | null>(null);

  const loadBill = async () => {
    setError("");
    setBill(null);
    setVisit(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/billing/${billId}`, {
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        let savedVisit: BillingVisit;
        if (data.visit_id) {
          const visitResponse = await fetch(`${BACKEND_URL}/api/visits/${data.visit_id}`, { credentials: "include" });
          if (!visitResponse.ok) throw new Error("Unable to load the visit details for this bill.");
          savedVisit = await visitResponse.json();
        } else {
          // Legacy bills have no linked clinical record; retain the same document layout.
          savedVisit = { id: 0, patient_id: data.patient_id, status: "", assigned_doctor: data.doctor };
        }
        setVisit(savedVisit);
        const transactionItems = (data.items || []).map((i: any) => {
          const totalAmount = Number(i.totalAmount ?? ((i.quantity || 0) * (i.medicine?.mrpPrice || 0)));
          return {
            ...i,
            item_type: "medicine",
            totalAmount,
          };
        });
        const extraItems = (data.bill_items || []).map((i: any) => ({
          ...i,
          item_type: i.item_type,
          description: i.description,
          quantity: i.quantity || 1,
          price: i.unit_price || 0,
          totalAmount: Number(i.total_price || 0),
          medicine: null,
        }));
        const items = [...transactionItems, ...extraItems];

        const totalAmount = items.reduce(
          (sum: number, i: any) => sum + i.totalAmount,
          0
        );

        setBill({ ...data, items, totalAmount });
        setPatient(data.patient || data.Patient);
        setBiller(data.biller);
      } else {
        throw new Error("Unable to load this bill.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load this bill.");
    }
  };

  const getMedicineLabel = (brand: string, type?: string) => {
  if (type === "Tablet") return `Tab. ${brand}`;
  if (type === "Capsule") return `Cap. ${brand}`;
  return brand;
};


  useEffect(() => {
    if (billId) loadBill();
  }, [billId]);

  useEffect(() => {
    if (!bill || !visit || !autoPrint) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      await document.fonts.ready;
      await Promise.all(Array.from(document.images).map((image) => image.decode().catch(() => undefined)));
      if (!cancelled) window.print();
    }, 500);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [bill, visit, autoPrint]);

  if (error) return <div role="alert" className="p-6 text-center text-red-600">{error}</div>;
  if (!bill || !visit) return <div className="text-center p-6">Loading...</div>;

  const contact = patient?.contact || patient?.Contact;

  return (
    <>
      <BillingPrintDocument
          visit={visit}
          billId={bill.id}
          contact={contact}
          date={new Date(bill.date).toLocaleDateString("en-GB")}
          billedBy={biller?.name || biller?.displayName || biller?.email || "-"}
          lines={bill.items.map((item, index) => ({
            key: `saved-bill-${index}`,
            type: item.medicine?.type === "Service" ? "service" : item.item_type || "medicine",
            description: item.item_type === "medicine"
              ? getMedicineLabel(item.medicine?.brand || item.medicine?.name || "-", item.medicine?.type)
              : item.description || "-",
            batchNo: item.medicine?.batchNo,
            expiryDate: item.medicine?.expiryDate,
            quantity: Number(item.quantity),
            unitPrice: Number(item.medicine?.mrpPrice ?? item.price ?? 0),
            totalAmount: item.totalAmount,
          }))}
          totalAmount={bill.totalAmount}
        />
      <div className="text-center print:hidden">
        <button onClick={() => window.print()} className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded">
          Print / Download PDF
        </button>
      </div>
    </>
  );
};

export default PrintBill;
